import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { 
  ClipboardList,
  Calendar, 
  Loader2, 
  Save, 
  ChevronRight, 
  ChevronDown,
  Plus,
  Trash2,
  Maximize2,
  Minimize2,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import GovernanceFeedbackSection from './GovernanceFeedbackSection';
import { DELIVERABLE_STATUS_OPTIONS, DEFAULT_PHASE_IDS } from './constants';

const API = process.env.REACT_APP_BACKEND_URL;

const ImplementationDialog = ({ pillar, open, onOpenChange, onSave, isAdmin, isLeadership, currentUser, scopePillar }) => {
  const [content, setContent] = useState(pillar?.content || {});
  const [saving, setSaving] = useState(false);
  const [collapsedPhases, setCollapsedPhases] = useState({});
  const [collapsedScopePhases, setCollapsedScopePhases] = useState({});
  const [collapsedItems, setCollapsedItems] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [showTaskForm, setShowTaskForm] = useState(null);
  const [taskForm, setTaskForm] = useState({ name: '', details: '', assignee: '', due_date: '' });
  const [platformUsers, setPlatformUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [allCollapsed, setAllCollapsed] = useState(true);
  const [newMilestoneInputs, setNewMilestoneInputs] = useState({});
  const [activeTab, setActiveTab] = useState('scope'); // 'scope' or 'milestones'

  useEffect(() => {
    if (pillar) setContent(pillar.content || {});
  }, [pillar]);

  // ============== SCOPE INTEGRATION ==============
  const getScopePhaseIds = () => {
    const customPhaseIds = scopePillar?.content?.custom_phases || [];
    return [...DEFAULT_PHASE_IDS, ...customPhaseIds];
  };

  const getScopePhaseName = (phaseId) => {
    if (scopePillar?.content?.phase_names?.[phaseId]) {
      return scopePillar.content.phase_names[phaseId];
    }
    const defaultIdx = DEFAULT_PHASE_IDS.indexOf(phaseId);
    if (defaultIdx !== -1) return `Phase ${defaultIdx + 1}`;
    return phaseId;
  };

  const SCOPE_PHASES = getScopePhaseIds().map(phaseId => ({
    id: phaseId,
    name: getScopePhaseName(phaseId),
    isCustom: !DEFAULT_PHASE_IDS.includes(phaseId),
  }));

  const getScopePhaseStatus = (phaseId) => scopePillar?.content?.phase_statuses?.[phaseId] || 'planning';
  const getScopePhaseDueDate = (phaseId) => scopePillar?.content?.phase_due_dates?.[phaseId] || '';
  const getScopeDeliverables = (phaseId) => scopePillar?.content?.phases?.[phaseId] || [];
  const getScopeSubPhases = (phaseId) => scopePillar?.content?.sub_phases?.[phaseId] || [];

  const getProjectedDate = (deliverableId) => content.projected_dates?.[deliverableId] || '';
  const updateProjectedDate = (deliverableId, date) => {
    const projectedDates = { ...(content.projected_dates || {}) };
    projectedDates[deliverableId] = date;
    setContent({ ...content, projected_dates: projectedDates });
  };

  const getScopePhaseStats = (phaseId) => {
    let totalDeliverables = 0, completedDeliverables = 0, totalTasks = 0, completedTasks = 0;
    const phaseDeliverables = getScopeDeliverables(phaseId);
    totalDeliverables += phaseDeliverables.length;
    completedDeliverables += phaseDeliverables.filter(d => d.status === 'completed').length;
    phaseDeliverables.forEach(d => {
      totalTasks += (d.tasks || []).length;
      completedTasks += (d.tasks || []).filter(t => t.completed).length;
    });
    const subPhases = getScopeSubPhases(phaseId);
    subPhases.forEach(sp => {
      const spDeliverables = sp.deliverables || [];
      totalDeliverables += spDeliverables.length;
      completedDeliverables += spDeliverables.filter(d => d.status === 'completed').length;
      spDeliverables.forEach(d => {
        totalTasks += (d.tasks || []).length;
        completedTasks += (d.tasks || []).filter(t => t.completed).length;
      });
    });
    const percentComplete = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 
      (totalDeliverables > 0 ? Math.round((completedDeliverables / totalDeliverables) * 100) : 0);
    return { totalDeliverables, completedDeliverables, totalTasks, completedTasks, percentComplete, subPhasesCount: subPhases.length };
  };

  const isDeliverableLate = (deliverable) => {
    if (deliverable.status === 'completed') return false;
    if (!deliverable.due_date) return false;
    return new Date(deliverable.due_date) < new Date();
  };

  // ============== IMPLEMENTATION PHASES ==============
  const defaultTeamMembers = [
    { id: 'ryan_march', name: 'Ryan March', role: 'Lead Developer', avatar: 'RM' },
    { id: 'ryan_adams', name: 'Ryan Adams', role: 'Developer', avatar: 'RA' },
    { id: 'gabe', name: 'Gabriel Gecan', role: 'Developer', avatar: 'GG' },
  ];

  const fetchPlatformUsers = async () => {
    if (platformUsers.length > 0) return;
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const users = response.data.map(u => ({
        id: u.id,
        name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
        role: u.role?.replace(/_/g, ' '),
        avatar: `${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase()
      }));
      setPlatformUsers(users);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const allTeamMembers = [...defaultTeamMembers, ...platformUsers.filter(pu => 
    !defaultTeamMembers.find(dm => dm.name.toLowerCase() === pu.name.toLowerCase())
  )];

  const IMPL_PHASES = [
    { id: 'foundation', name: 'Foundation', color: 'emerald' },
    { id: 'core_features', name: 'Core Features', color: 'blue' },
    { id: 'advanced', name: 'Advanced Features', color: 'purple' },
    { id: 'polish', name: 'Polish & Launch', color: 'amber' },
  ];

  const normalizePhases = (content) => {
    if (content.phases && typeof content.phases === 'object') return content.phases;
    const phases = {};
    IMPL_PHASES.forEach(p => { phases[p.id] = []; });
    if (content.key_milestones?.length > 0) {
      phases.foundation = content.key_milestones.map(m => ({ ...m, tasks: m.tasks || [] }));
    }
    return phases;
  };

  const phases = normalizePhases(content);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(pillar.id, { content: { ...content, phases } });
      toast.success('Implementation plan updated');
      onOpenChange(false);
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Milestone management
  const addMilestone = (phaseId) => {
    const inputValue = newMilestoneInputs[phaseId];
    if (!inputValue?.trim()) return;
    const newMs = { id: Date.now(), name: inputValue.trim(), status: 'pending', due_date: '', progress: 0, tasks: [] };
    const updatedPhases = { ...phases, [phaseId]: [...(phases[phaseId] || []), newMs] };
    setContent({ ...content, phases: updatedPhases });
    setNewMilestoneInputs(prev => ({ ...prev, [phaseId]: '' }));
  };

  const updateMilestone = (phaseId, idx, field, value) => {
    const updated = [...(phases[phaseId] || [])];
    updated[idx] = { ...updated[idx], [field]: value };
    setContent({ ...content, phases: { ...phases, [phaseId]: updated } });
  };

  const removeMilestone = (phaseId, idx) => {
    const updated = (phases[phaseId] || []).filter((_, i) => i !== idx);
    setContent({ ...content, phases: { ...phases, [phaseId]: updated } });
  };

  // Task management
  const addTask = (phaseId, milestoneIdx, taskData) => {
    if (!taskData.name?.trim()) return;
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const newTask = { 
      id: Date.now(), name: taskData.name.trim(), details: taskData.details || '',
      assignee: taskData.assignee || '', due_date: taskData.due_date || '',
      completed: false, assigned_by: userName, assigned_at: new Date().toISOString(),
    };
    const phaseItems = [...(phases[phaseId] || [])];
    const tasks = [...(phaseItems[milestoneIdx].tasks || []), newTask];
    phaseItems[milestoneIdx] = { ...phaseItems[milestoneIdx], tasks };
    const completedCount = tasks.filter(t => t.completed).length;
    phaseItems[milestoneIdx].progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseItems } });
    setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
    setShowTaskForm(null);
  };

  const toggleTask = (phaseId, milestoneIdx, taskId) => {
    const phaseItems = [...(phases[phaseId] || [])];
    const completedBy = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const tasks = phaseItems[milestoneIdx].tasks.map(t => 
      t.id === taskId ? { ...t, completed: !t.completed, completed_by: !t.completed ? completedBy : null, completed_at: !t.completed ? new Date().toISOString() : null } : t
    );
    phaseItems[milestoneIdx] = { ...phaseItems[milestoneIdx], tasks };
    const completedCount = tasks.filter(t => t.completed).length;
    phaseItems[milestoneIdx].progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
    if (phaseItems[milestoneIdx].progress === 100) phaseItems[milestoneIdx].status = 'completed';
    else if (phaseItems[milestoneIdx].progress > 0) phaseItems[milestoneIdx].status = 'in_progress';
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseItems } });
  };

  const removeTask = (phaseId, milestoneIdx, taskId) => {
    const phaseItems = [...(phases[phaseId] || [])];
    const tasks = phaseItems[milestoneIdx].tasks.filter(t => t.id !== taskId);
    phaseItems[milestoneIdx] = { ...phaseItems[milestoneIdx], tasks };
    const completedCount = tasks.filter(t => t.completed).length;
    phaseItems[milestoneIdx].progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseItems } });
  };

  // Progress calculations
  const calculateOverallProgress = () => {
    let totalTasks = 0, completedTasks = 0;
    IMPL_PHASES.forEach(phase => {
      (phases[phase.id] || []).forEach(m => {
        if (m.tasks && m.tasks.length > 0) {
          totalTasks += m.tasks.length;
          completedTasks += m.tasks.filter(t => t.completed).length;
        }
      });
    });
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const calculatePhaseProgress = (phaseId) => {
    let totalTasks = 0, completedTasks = 0;
    (phases[phaseId] || []).forEach(m => {
      if (m.tasks && m.tasks.length > 0) {
        totalTasks += m.tasks.length;
        completedTasks += m.tasks.filter(t => t.completed).length;
      }
    });
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const getMilestoneProgress = (milestone) => {
    const tasks = milestone?.tasks || [];
    if (tasks.length === 0) return 0;
    return Math.round((tasks.filter(t => t.completed).length / tasks.length) * 100);
  };

  const overallProgress = calculateOverallProgress();
  const statusOptions = DELIVERABLE_STATUS_OPTIONS;

  const togglePhaseCollapse = (phaseId) => setCollapsedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  const toggleScopePhaseCollapse = (phaseId) => setCollapsedScopePhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  const toggleItemCollapse = (phaseId, idx) => {
    const key = `${phaseId}-${idx}`;
    setCollapsedItems(prev => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const getPhaseColor = (phaseId) => {
    const phase = IMPL_PHASES.find(p => p.id === phaseId);
    const colorMap = {
      emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
      blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
      purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400' },
      amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' },
    };
    return colorMap[phase?.color] || colorMap.emerald;
  };

  const getScopePhaseColor = (phaseId) => {
    const status = getScopePhaseStatus(phaseId);
    if (status === 'completed') return 'bg-green-500/15 border-green-500/40';
    if (['building', 'coding', 'internal_testing', 'team_testing', 'debug'].includes(status)) return 'bg-blue-500/10 border-blue-500/30';
    return 'bg-slate-500/10 border-slate-500/30';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-emerald-500" /> Implementation Plan
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between">
            <span>Development methodology and progress tracking</span>
            <div className="flex items-center gap-2">
              <Progress value={overallProgress} className="w-24 h-2" />
              <span className="text-xs font-medium text-emerald-600">{overallProgress}%</span>
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Tab Switcher */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg">
          <button 
            onClick={() => setActiveTab('scope')}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'scope' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Scope View
          </button>
          <button 
            onClick={() => setActiveTab('milestones')}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'milestones' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Milestones & Tasks
          </button>
        </div>

        <div className="space-y-4 py-4">
          {/* Metadata */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Methodology</Label>
              <Input value={content.methodology || ''} onChange={(e) => setContent({...content, methodology: e.target.value})} disabled={!isAdmin} className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Current Sprint</Label>
              <Input value={content.current_sprint || ''} onChange={(e) => setContent({...content, current_sprint: e.target.value})} disabled={!isAdmin} className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Team Size</Label>
              <Input value={content.team_size || ''} onChange={(e) => setContent({...content, team_size: e.target.value})} disabled={!isAdmin} className="mt-1 h-8 text-sm" />
            </div>
          </div>

          {/* ============== SCOPE VIEW TAB ============== */}
          {activeTab === 'scope' && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Scope Phases & Deliverables</Label>
              {SCOPE_PHASES.map((phase) => {
                const stats = getScopePhaseStats(phase.id);
                const status = getScopePhaseStatus(phase.id);
                const dueDate = getScopePhaseDueDate(phase.id);
                const isCollapsed = collapsedScopePhases[phase.id] !== false;
                const phaseDeliverables = getScopeDeliverables(phase.id);
                const subPhases = getScopeSubPhases(phase.id);

                return (
                  <div key={phase.id} className={`rounded-lg border-2 ${getScopePhaseColor(phase.id)} overflow-hidden`}>
                    {/* Phase Header */}
                    <div className="p-3 cursor-pointer hover:bg-white/5" onClick={() => toggleScopePhaseCollapse(phase.id)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        <div className={`w-3 h-3 rounded-full ${statusOptions.find(s => s.value === status)?.color || 'bg-slate-400'}`} />
                        <span className="font-semibold text-sm flex-1">{phase.name}</span>
                        <Badge variant="outline" className="text-[10px]">{statusOptions.find(s => s.value === status)?.label || 'Planning'}</Badge>
                        {dueDate && <Badge variant="outline" className="text-[10px]"><Calendar className="h-3 w-3 mr-1" />{new Date(dueDate).toLocaleDateString()}</Badge>}
                        <Badge variant="secondary" className="text-[10px]">{stats.totalDeliverables} deliverables</Badge>
                        {stats.subPhasesCount > 0 && <Badge variant="outline" className="text-[10px] border-cyan-500/50 text-cyan-500">{stats.subPhasesCount} sub-phases</Badge>}
                        <Badge variant="outline" className={`text-[10px] ${stats.completedTasks > 0 ? 'border-green-500/50 text-green-600' : ''}`}>{stats.completedTasks}/{stats.totalTasks} tasks</Badge>
                        <span className="text-xs font-bold">{stats.percentComplete}%</span>
                      </div>
                      <Progress value={stats.percentComplete} className="h-1.5 mt-2" />
                    </div>

                    {/* Expanded Content */}
                    {!isCollapsed && (
                      <div className="border-t border-white/10 p-3 space-y-3 bg-black/10">
                        {/* Implementation Notes */}
                        {isAdmin && (
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Implementation Notes</Label>
                            <Input 
                              value={content.impl_notes?.[phase.id] || ''} 
                              onChange={(e) => setContent({...content, impl_notes: {...(content.impl_notes || {}), [phase.id]: e.target.value}})}
                              className="h-7 text-xs mt-1" placeholder="Add implementation notes..."
                            />
                          </div>
                        )}

                        {/* Phase Deliverables */}
                        {phaseDeliverables.length > 0 && (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <Label className="text-[10px] text-muted-foreground">Deliverables</Label>
                              <span className="text-[9px] text-muted-foreground">Due → Projected</span>
                            </div>
                            {phaseDeliverables.map((deliverable, idx) => {
                              const deliverableId = deliverable.id || `phase_${phase.id}_del_${idx}`;
                              const projectedDate = getProjectedDate(deliverableId);
                              return (
                                <div key={deliverableId} className={`p-2 rounded text-xs ${deliverable.status === 'completed' ? 'bg-green-500/10' : isDeliverableLate(deliverable) ? 'bg-red-500/10' : 'bg-white/5'}`}>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${statusOptions.find(s => s.value === deliverable.status)?.color || 'bg-slate-400'}`} />
                                    <span className={`flex-1 truncate ${deliverable.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{deliverable.name}</span>
                                    <Badge variant="outline" className={`text-[9px] ${(deliverable.tasks?.filter(t => t.completed).length || 0) > 0 ? 'border-green-500/50 text-green-500' : ''}`}>
                                      {deliverable.tasks?.filter(t => t.completed).length || 0}/{deliverable.tasks?.length || 0}
                                    </Badge>
                                    <Badge variant="outline" className="text-[9px]">{statusOptions.find(s => s.value === deliverable.status)?.label || 'Planning'}</Badge>
                                  </div>
                                  <div className="flex items-center gap-3 mt-1.5 pl-4">
                                    <div className="flex items-center gap-1">
                                      <Calendar className={`h-3 w-3 ${isDeliverableLate(deliverable) ? 'text-red-500' : 'text-muted-foreground'}`} />
                                      <span className="text-[10px] text-muted-foreground">Due:</span>
                                      <span className={`text-[10px] ${isDeliverableLate(deliverable) ? 'text-red-500' : 'text-muted-foreground'}`}>
                                        {deliverable.due_date ? new Date(deliverable.due_date).toLocaleDateString() : 'Not set'}
                                      </span>
                                    </div>
                                    <span className="text-muted-foreground">→</span>
                                    <div className="flex items-center gap-1">
                                      <Clock className={`h-3 w-3 ${projectedDate && deliverable.due_date && new Date(projectedDate) > new Date(deliverable.due_date) ? 'text-amber-500' : 'text-blue-400'}`} />
                                      <span className="text-[10px] text-muted-foreground">Projected:</span>
                                      {isAdmin ? (
                                        <Input type="date" value={projectedDate} onChange={(e) => updateProjectedDate(deliverableId, e.target.value)}
                                          className={`h-5 text-[10px] w-[100px] ${projectedDate && deliverable.due_date && new Date(projectedDate) > new Date(deliverable.due_date) ? 'border-amber-500/50 text-amber-500' : ''}`}
                                        />
                                      ) : (
                                        <span className={`text-[10px] ${projectedDate && deliverable.due_date && new Date(projectedDate) > new Date(deliverable.due_date) ? 'text-amber-500' : 'text-blue-400'}`}>
                                          {projectedDate ? new Date(projectedDate).toLocaleDateString() : 'Not set'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Sub-Phases */}
                        {subPhases.length > 0 && (
                          <div className="space-y-2">
                            <Label className="text-[10px] text-muted-foreground">Sub-Phases</Label>
                            {subPhases.map((subPhase) => {
                              const spDeliverables = subPhase.deliverables || [];
                              const spTasksTotal = spDeliverables.reduce((acc, d) => acc + (d.tasks || []).length, 0);
                              const spTasksCompleted = spDeliverables.reduce((acc, d) => acc + (d.tasks || []).filter(t => t.completed).length, 0);
                              return (
                                <div key={subPhase.id} className="p-2 rounded bg-cyan-500/10 border border-cyan-500/30">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <div className={`w-2 h-2 rounded-full ${statusOptions.find(s => s.value === subPhase.status)?.color || 'bg-slate-400'}`} />
                                    <span className="text-xs font-medium text-cyan-300 flex-1">{subPhase.name}</span>
                                    {subPhase.due_date && <span className="text-[10px] text-muted-foreground"><Calendar className="h-3 w-3 inline mr-1" />{new Date(subPhase.due_date).toLocaleDateString()}</span>}
                                    <Badge variant="outline" className="text-[9px]">{spDeliverables.length} del.</Badge>
                                    <Badge variant="outline" className={`text-[9px] ${spTasksCompleted > 0 ? 'border-green-500/50 text-green-500' : ''}`}>{spTasksCompleted}/{spTasksTotal} tasks</Badge>
                                  </div>
                                  {spDeliverables.length > 0 && (
                                    <div className="mt-2 pl-4 space-y-1">
                                      {spDeliverables.map((deliverable, dIdx) => {
                                        const spDeliverableId = deliverable.id || `sp_${subPhase.id}_del_${dIdx}`;
                                        const spProjectedDate = getProjectedDate(spDeliverableId);
                                        return (
                                          <div key={spDeliverableId} className={`p-1.5 rounded text-[11px] ${deliverable.status === 'completed' ? 'bg-green-500/10' : isDeliverableLate(deliverable) ? 'bg-red-500/10' : 'bg-white/5'}`}>
                                            <div className="flex items-center gap-2">
                                              <div className={`w-1.5 h-1.5 rounded-full ${statusOptions.find(s => s.value === deliverable.status)?.color || 'bg-slate-400'}`} />
                                              <span className={`flex-1 truncate ${deliverable.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{deliverable.name}</span>
                                              <span className="text-[9px] text-muted-foreground">{deliverable.tasks?.filter(t => t.completed).length || 0}/{deliverable.tasks?.length || 0}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1 pl-3 text-[9px]">
                                              <span className={isDeliverableLate(deliverable) ? 'text-red-500' : 'text-muted-foreground'}>Due: {deliverable.due_date ? new Date(deliverable.due_date).toLocaleDateString() : '-'}</span>
                                              <span className="text-muted-foreground">→</span>
                                              {isAdmin ? (
                                                <div className="flex items-center gap-1">
                                                  <Clock className={`h-2.5 w-2.5 ${spProjectedDate && deliverable.due_date && new Date(spProjectedDate) > new Date(deliverable.due_date) ? 'text-amber-500' : 'text-blue-400'}`} />
                                                  <Input type="date" value={spProjectedDate} onChange={(e) => updateProjectedDate(spDeliverableId, e.target.value)}
                                                    className={`h-4 text-[9px] w-[90px] ${spProjectedDate && deliverable.due_date && new Date(spProjectedDate) > new Date(deliverable.due_date) ? 'border-amber-500/50 text-amber-500' : ''}`}
                                                  />
                                                </div>
                                              ) : (
                                                <span className={spProjectedDate && deliverable.due_date && new Date(spProjectedDate) > new Date(deliverable.due_date) ? 'text-amber-500' : 'text-blue-400'}>
                                                  Proj: {spProjectedDate ? new Date(spProjectedDate).toLocaleDateString() : '-'}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {phaseDeliverables.length === 0 && subPhases.length === 0 && (
                          <p className="text-xs text-muted-foreground text-center py-2">No deliverables in this phase</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ============== MILESTONES TAB ============== */}
          {activeTab === 'milestones' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Implementation Phases</Label>
                <Button size="sm" variant="outline" onClick={() => {
                  if (allCollapsed) {
                    setCollapsedPhases({});
                    const allItemsExpanded = {};
                    IMPL_PHASES.forEach(phase => { (phases[phase.id] || []).forEach((_, idx) => { allItemsExpanded[`${phase.id}-${idx}`] = false; }); });
                    setCollapsedItems(allItemsExpanded);
                  } else {
                    const allPhasesCollapsed = {};
                    IMPL_PHASES.forEach(phase => { allPhasesCollapsed[phase.id] = true; });
                    setCollapsedPhases(allPhasesCollapsed);
                    setCollapsedItems({});
                  }
                  setAllCollapsed(!allCollapsed);
                }}>
                  {allCollapsed ? <Maximize2 className="h-3 w-3 mr-1" /> : <Minimize2 className="h-3 w-3 mr-1" />}
                  {allCollapsed ? 'Expand All' : 'Collapse All'}
                </Button>
              </div>

              {IMPL_PHASES.map((phase) => {
                const phaseColors = getPhaseColor(phase.id);
                const isPhaseCollapsed = collapsedPhases[phase.id];
                const phaseProgress = calculatePhaseProgress(phase.id);
                const milestoneCount = (phases[phase.id] || []).length;
                const completedMilestones = (phases[phase.id] || []).filter(m => m.status === 'completed').length;

                return (
                  <div key={phase.id} className={`rounded-lg border-2 ${phaseColors.border} ${phaseColors.bg}`}>
                    <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5" onClick={() => togglePhaseCollapse(phase.id)}>
                      <div className="flex items-center gap-2">
                        {isPhaseCollapsed ? <ChevronRight className={`h-4 w-4 ${phaseColors.text}`} /> : <ChevronDown className={`h-4 w-4 ${phaseColors.text}`} />}
                        <span className="font-semibold">{phase.name}</span>
                        <Badge variant="outline" className={`text-[10px] ${phaseColors.border}`}>{completedMilestones}/{milestoneCount} milestones</Badge>
                        {phaseProgress > 0 && <Badge className={`text-[10px] ${phaseProgress === 100 ? 'bg-green-500' : 'bg-amber-500'}`}>{phaseProgress}%</Badge>}
                      </div>
                      <Progress value={phaseProgress} className="w-24 h-1.5" />
                    </div>

                    {!isPhaseCollapsed && (
                      <div className="px-3 pb-3 space-y-2">
                        {(phases[phase.id] || []).map((milestone, i) => {
                          const itemKey = `${phase.id}-${i}`;
                          const isItemCollapsed = collapsedItems[itemKey] !== false;
                          const taskCount = milestone.tasks?.length || 0;
                          const completedTasks = milestone.tasks?.filter(t => t.completed).length || 0;

                          return (
                            <div key={milestone.id || i} className="rounded-lg border bg-slate-800/50">
                              <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => toggleItemCollapse(phase.id, i)}>
                                <div className={`w-2.5 h-2.5 rounded-full ${statusOptions.find(s => s.value === milestone.status)?.color || 'bg-slate-400'}`} />
                                <span className="font-medium text-sm flex-1">{milestone.name}</span>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {taskCount > 0 && <Badge variant="outline" className="text-[10px]">{completedTasks}/{taskCount} tasks</Badge>}
                                  {milestone.due_date && <span className={new Date(milestone.due_date) < new Date() && milestone.status !== 'completed' ? 'text-red-400' : ''}>Due: {new Date(milestone.due_date).toLocaleDateString()}</span>}
                                  {isItemCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </div>
                              </div>

                              {!isItemCollapsed && (
                                <div className="px-3 pb-3 pt-0 space-y-3 border-t border-slate-700">
                                  {isAdmin && (
                                    <div className="grid grid-cols-4 gap-2 pt-2">
                                      <div className="col-span-2">
                                        <Label className="text-[10px] text-muted-foreground">Name</Label>
                                        <Input value={milestone.name} onChange={(e) => updateMilestone(phase.id, i, 'name', e.target.value)} className="h-8 text-xs" />
                                      </div>
                                      <div>
                                        <Label className="text-[10px] text-muted-foreground">Status</Label>
                                        <Select value={milestone.status || 'pending'} onValueChange={(v) => updateMilestone(phase.id, i, 'status', v)}>
                                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            {statusOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <Label className="text-[10px] text-muted-foreground">Due Date</Label>
                                        <Input type="date" value={milestone.due_date || ''} onChange={(e) => updateMilestone(phase.id, i, 'due_date', e.target.value)} className="h-8 text-xs" />
                                      </div>
                                    </div>
                                  )}

                                  <div className="flex items-center gap-2">
                                    <Progress value={getMilestoneProgress(milestone)} className="flex-1 h-1.5" />
                                    <span className="text-xs text-muted-foreground">{getMilestoneProgress(milestone)}%</span>
                                    {isAdmin && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeMilestone(phase.id, i)}><Trash2 className="h-3 w-3 text-red-400" /></Button>}
                                  </div>

                                  <div className="pt-2 border-t border-slate-700">
                                    <div className="flex items-center justify-between mb-2">
                                      <Label className="text-xs flex items-center gap-2">Tasks <Badge variant="secondary" className="text-[10px]">{completedTasks}/{taskCount}</Badge></Label>
                                      {(isAdmin || isLeadership) && (
                                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={(e) => {
                                          e.stopPropagation();
                                          setShowTaskForm(showTaskForm?.phaseId === phase.id && showTaskForm?.milestoneIdx === i ? null : { phaseId: phase.id, milestoneIdx: i });
                                          setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
                                          fetchPlatformUsers();
                                        }}>
                                          <Plus className="h-3 w-3 mr-1" /> Add Task
                                        </Button>
                                      )}
                                    </div>

                                    <div className="space-y-1">
                                      {milestone.tasks?.map((task) => {
                                        const isTaskExpanded = expandedTasks[task.id];
                                        return (
                                          <div key={task.id} className={`p-2 rounded border ${task.completed ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-700/50 border-slate-600'}`}>
                                            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setExpandedTasks(prev => ({ ...prev, [task.id]: !prev[task.id] }))}>
                                              <Checkbox checked={task.completed} onCheckedChange={() => toggleTask(phase.id, i, task.id)} onClick={(e) => e.stopPropagation()} disabled={!isAdmin && !isLeadership} />
                                              <span className={`flex-1 text-sm ${task.completed ? 'line-through text-muted-foreground' : ''}`}>{task.name}</span>
                                              {task.assignee && <Badge variant="outline" className="text-[10px]">{allTeamMembers.find(m => m.id === task.assignee)?.name || task.assignee}</Badge>}
                                              {isTaskExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                            </div>
                                            {isTaskExpanded && (
                                              <div className="mt-2 pt-2 border-t border-slate-600 text-xs space-y-1">
                                                {task.details && <p className="text-muted-foreground">{task.details}</p>}
                                                <div className="flex items-center gap-3 text-muted-foreground">
                                                  {task.assigned_by && <span>Assigned by: {task.assigned_by}</span>}
                                                  {task.due_date && <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>}
                                                  {task.completed_by && <span className="text-green-400">✓ {task.completed_by}</span>}
                                                </div>
                                                {(isAdmin || isLeadership) && <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-400" onClick={() => removeTask(phase.id, i, task.id)}><Trash2 className="h-3 w-3 mr-1" /> Remove</Button>}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>

                                    {showTaskForm?.phaseId === phase.id && showTaskForm?.milestoneIdx === i && (
                                      <div className="mt-2 p-2 bg-slate-700/50 rounded border border-slate-600 space-y-2">
                                        <Input value={taskForm.name} onChange={(e) => setTaskForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Task name..." className="h-8 text-xs" />
                                        <Textarea value={taskForm.details} onChange={(e) => setTaskForm(prev => ({ ...prev, details: e.target.value }))} placeholder="Details (optional)..." rows={2} className="text-xs" />
                                        <div className="grid grid-cols-2 gap-2">
                                          <Select value={taskForm.assignee} onValueChange={(v) => setTaskForm(prev => ({ ...prev, assignee: v }))}>
                                            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Assign to..." /></SelectTrigger>
                                            <SelectContent>{allTeamMembers.map(member => <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>)}</SelectContent>
                                          </Select>
                                          <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))} className="h-8 text-xs" />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowTaskForm(null)}>Cancel</Button>
                                          <Button size="sm" className="h-7 text-xs" onClick={() => addTask(phase.id, i, taskForm)} disabled={!taskForm.name?.trim()}><Plus className="h-3 w-3 mr-1" /> Create</Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {isAdmin && (
                          <div className="flex gap-2">
                            <Input value={newMilestoneInputs[phase.id] || ''} onChange={(e) => setNewMilestoneInputs(prev => ({ ...prev, [phase.id]: e.target.value }))}
                              placeholder="Add milestone..." className="h-8 text-xs" onKeyDown={(e) => e.key === 'Enter' && addMilestone(phase.id)} />
                            <Button size="sm" className="h-8" onClick={() => addMilestone(phase.id)}><Plus className="h-4 w-4" /></Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Feedback Section */}
          <GovernanceFeedbackSection pillar={pillar} isAdmin={isAdmin} isLeadership={isLeadership} currentUser={currentUser} onSave={onSave} />
        </div>

        {(isAdmin || isLeadership) && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { ImplementationDialog };
